import { browser } from '$app/environment';

/**
 * Track an event with Umami analytics
 * 
 * @param eventName The name of the event to track
 * @param eventData Optional data to attach to the event
 */
export function trackEvent(eventName: string, eventData?: Record<string, string | number | boolean>): void {
    if (browser && typeof window !== 'undefined') {
        // @ts-ignore - Umami is loaded via script tag
        if (window.umami) window.umami.track(eventName, eventData);
    }
}

/**
 * Track a search event
 * 
 * @param query The search query string
 * @param filters Optional filters applied to the search
 * @param resultsCount Number of results returned
 */
export function trackSearch(
    query: string,
    filters?: {
        tags?: string[],
        origin?: string,
        sort?: string
    },
    resultsCount?: number
): void {
    trackEvent('search', {
        query,
        tags: filters?.tags?.join(',') || '',
        origin: filters?.origin || '',
        sort: filters?.sort || 'relevance',
        resultsCount: resultsCount || 0
    });
}

/**
 * Track a click on a trail
 * 
 * @param trailName The name of the trail that was clicked
 * @param orgName The organization that owns the trail
 */
export function trackTrailClick(trailName: string, orgName: string): void {
    trackEvent('trail_click', {
        trail: trailName,
        org: orgName
    });
}

/**
 * Track a version change
 * 
 * @param version The new version selected
 * @param trailName The name of the trail
 * @param orgName The organization name
 */
export function trackVersionChange(version: string, trailName?: string, orgName?: string): void {
    trackEvent('version_change', {
        version,
        trail: trailName || '',
        org: orgName || ''
    });
}

/**
 * Track an action change
 * 
 * @param action The action that was selected
 * @param trailName The name of the trail
 * @param orgName The organization name
 * @param version The current version
 */
export function trackActionChange(
    action: string,
    trailName?: string,
    orgName?: string,
    version?: string
): void {
    trackEvent('action_change', {
        action,
        trail: trailName || '',
        org: orgName || '',
        version: version || ''
    });
}

/**
 * Track a tab change in the quick run panel
 * 
 * @param tab The tab that was selected (cli, mcp, mcp-sse)
 * @param trailName The name of the trail
 * @param orgName The organization name
 */
export function trackTabChange(tab: string, trailName?: string, orgName?: string): void {
    trackEvent('quickrun_tab_change', {
        tab,
        trail: trailName || '',
        org: orgName || ''
    });
}

/**
 * Track a code copy event
 * 
 * @param type The type of code copied (install, usage)
 * @param trailName The name of the trail
 * @param orgName The organization name
 * @param action The current action (if any)
 */
export function trackCodeCopy(
    type: string,
    trailName?: string,
    orgName?: string,
    action?: string
): void {
    trackEvent('copy_code', {
        type,
        trail: trailName || '',
        org: orgName || '',
        action: action || ''
    });
}

/**
 * Track a click on an origin link
 * 
 * @param origin The origin domain that was clicked
 * @param trailName The name of the trail
 * @param orgName The organization name
 */
export function trackOriginClick(origin: string, trailName?: string, orgName?: string): void {
    trackEvent('origin_click', {
        origin,
        trail: trailName || '',
        org: orgName || ''
    });
} 